import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { JobSearchResult } from '@/types/jobSearch';
import { 
  Briefcase, MapPin, CalendarDays, DollarSign, ExternalLink, ListChecks, Users, ShieldCheck, Info,
  Bookmark, BookmarkCheck, Hourglass
} from 'lucide-react';
import { toast } from 'sonner';

interface JobDetailsDialogProps {
  job: JobSearchResult | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isJobSaved?: boolean;
  onSaveJob?: () => Promise<void> | void;
  isSavingJob?: boolean;
  isUserLoggedIn?: boolean;
}

const DetailSection: React.FC<{ title: string; icon?: React.ElementType; children: React.ReactNode }> = ({ title, icon: Icon, children }) => (
  <div className="mb-4">
    <h3 className="text-md font-semibold mb-1 flex items-center">
      {Icon && <Icon className="h-4 w-4 mr-2 text-primary" />}
      {title}
    </h3>
    <div className="text-sm text-muted-foreground pl-1">{children}</div>
  </div>
);

export const JobDetailsDialog: React.FC<JobDetailsDialogProps> = ({ 
  job, isOpen, onOpenChange,
  isJobSaved, onSaveJob, isSavingJob, isUserLoggedIn
}) => {
  if (!job) return null;

  const handleSaveClick = async () => {
    if (!isUserLoggedIn) {
      toast.error("Please log in to save jobs.");
      return;
    }
    if (onSaveJob) {
      await onSaveJob();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl lg:max-w-4xl">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle className="text-xl flex items-center">
              <Briefcase className="h-6 w-6 mr-2 text-primary" />
              {job.title || job.job_title || 'Job Details'}
            </DialogTitle>
            {isUserLoggedIn && onSaveJob && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSaveClick}
                disabled={isSavingJob}
                aria-label={isJobSaved ? "Unsave job" : "Save job"}
                className="h-8 w-8"
              >
                {isSavingJob ? (
                  <Hourglass className="h-4 w-4 animate-spin" />
                ) : isJobSaved ? (
                  <BookmarkCheck className="h-5 w-5 text-primary" />
                ) : (
                  <Bookmark className="h-5 w-5 text-muted-foreground hover:text-primary" />
                )}
              </Button>
            )}
          </div>
          {job.company_name && (
            <DialogDescription className="text-md">{job.company_name}</DialogDescription>
          )}
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
            {job.location && (
              <div className="flex items-center text-sm">
                <MapPin className="h-4 w-4 mr-2 text-muted-foreground" /> {job.location}
              </div>
            )}
            {job.posted_date && (
              <div className="flex items-center text-sm">
                <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" /> Posted: {new Date(job.posted_date).toLocaleDateString()}
              </div>
            )}
            {job.salary_range && (
              <div className="flex items-center text-sm">
                <DollarSign className="h-4 w-4 mr-2 text-muted-foreground" /> {job.salary_range}
              </div>
            )}
            {job.job_type && (
              <div className="flex items-center text-sm">
                <Info className="h-4 w-4 mr-2 text-muted-foreground" /> Type: {job.job_type}
              </div>
            )}
             {job.source && (
                <div className="flex items-center text-sm">
                    <Briefcase className="h-4 w-4 mr-2 text-muted-foreground" /> Source: {job.source}
                </div>
            )}
            {(job.url || job.job_url) && (
                <a
                  href={job.url || job.job_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center col-span-full md:col-span-1"
                >
                  <ExternalLink className="ml-0 mr-2 h-4 w-4" /> View Original Post
                </a>
            )}
          </div>
          
          {job.description && (
            <DetailSection title="Full Description" icon={Info}>
              <p className="whitespace-pre-wrap">{job.description}</p>
            </DetailSection>
          )}

          {job.qualification_details && (
            <DetailSection title="Qualifications" icon={ShieldCheck}>
              <p className="whitespace-pre-wrap">{job.qualification_details}</p>
            </DetailSection>
          )}
          
          {job.responsibility_details && (
            <DetailSection title="Responsibilities" icon={ListChecks}>
              <p className="whitespace-pre-wrap">{job.responsibility_details}</p>
            </DetailSection>
          )}

          {job.benefits && Array.isArray(job.benefits) && job.benefits.length > 0 && (
            <DetailSection title="Benefits" icon={Users}>
              <div className="flex flex-wrap gap-2">
                {job.benefits.map((benefit, index) => (
                  <Badge key={index} variant="secondary">{benefit}</Badge>
                ))}
              </div>
            </DetailSection>
          )}
          {job.benefits && typeof job.benefits === 'string' && (
             <DetailSection title="Benefits" icon={Users}>
              <p>{job.benefits}</p>
            </DetailSection>
          )}


        </ScrollArea>

        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
